import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  AlertColor,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import { createTimestamp } from '../../../utils/firebaseUtils';
import { surgicalCaseService } from '../../../services/surgicalCaseService';
import { surgicalPhasesService } from '../../../services/surgicalPhasesService';
import { useUserData } from '../../../hooks/useUserData';

interface SurgicalProcedureData {
  id: string;
  procedureType: string;
  specialty: string;
  status: string;
  orderDate: Date;
  estimatedDuration?: number;
  requiresConsent?: boolean;
  specialInstructions?: string;
  orderingProvider?: string;
  priority?: string;
}

interface Patient {
  id: string;
  name: string;
  mrn?: string;
}

interface SurgicalPACUPhaseProps {
  surgicalProcedure: SurgicalProcedureData;
  patient: Patient;
  encounterId?: string;
}

const SurgicalPACUPhase: React.FC<SurgicalPACUPhaseProps> = ({ 
  surgicalProcedure, 
  patient, 
  encounterId 
}) => {
  const { user } = useUserData();
  const [checklistStatus, setChecklistStatus] = useState({
    postOpAssessment: 'pending',
    painManagement: 'pending',
    vitalSigns: 'pending',
    fluidBalance: 'pending',
    dischargeReadiness: 'pending'
  });

  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [loading, setLoading] = useState(false);
  const [pacuStartTime] = useState(new Date());
  const [recoveryTime, setRecoveryTime] = useState(0);

  // Vital signs and assessment states
  const [vitalSigns, setVitalSigns] = useState({
    bloodPressureSystolic: '',
    bloodPressureDiastolic: '',
    heartRate: '',
    respiratoryRate: '',
    oxygenSaturation: '',
    temperature: ''
  });
  const [painScore, setPainScore] = useState(0);
  const [aldreteScore, setAldreteScore] = useState(0);
  const [assessmentNotes, setAssessmentNotes] = useState('');
  const [fluidIntake, setFluidIntake] = useState('');
  const [fluidOutput, setFluidOutput] = useState('');
  const [painNotes, setPainNotes] = useState('');
  const [dischargeNotes, setDischargeNotes] = useState('');

  // Aldrete Score components
  const [aldreteComponents, setAldreteComponents] = useState({
    activity: 0,
    respiration: 0,
    circulation: 0,
    consciousness: 0,
    oxygenSaturation: 0
  });

  // Timer for recovery time
  useEffect(() => {
    const timer = setInterval(() => {
      const elapsed = Math.floor((new Date().getTime() - pacuStartTime.getTime()) / 60000);
      setRecoveryTime(elapsed);
    }, 60000);

    return () => clearInterval(timer);
  }, [pacuStartTime]);

  const showSnackbar = (message: string, severity: AlertColor = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handler functions
  const handleCompleteAssessment = () => {
    setOpenDialog('assessment');
  };

  const handleSaveAssessment = async () => {
    if (!assessmentNotes.trim()) {
      showSnackbar('Please enter assessment notes', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare assessment data
      const assessmentData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        assessmentNotes: assessmentNotes,
        assessedBy: user?.name || 'Unknown',
        recoveryTime: recoveryTime
      };

      // Use service to create PACU assessment
      await surgicalPhasesService.createPACUAssessment(assessmentData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, postOpAssessment: 'completed' }));
      setOpenDialog(null);
      setAssessmentNotes('');
      showSnackbar('Post-operative assessment completed');
    } catch (error) {
      console.error('Error saving assessment:', error);
      showSnackbar('Failed to save assessment', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentPainScore = () => {
    setOpenDialog('pain');
  };

  const handleSavePainScore = async () => {
    try {
      setLoading(true);

      // Prepare pain assessment data
      const painData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        painScore: painScore,
        painNotes: painNotes,
        assessedBy: user?.name || 'Unknown',
        interventions: painScore > 5 ? 'Pain management intervention required' : 'Pain well controlled'
      };

      // Use service to create pain assessment
      await surgicalPhasesService.createPainAssessment(painData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, painManagement: 'completed' }));
      setOpenDialog(null);
      setPainNotes('');
      showSnackbar(`Pain score ${painScore}/10 documented successfully`);
    } catch (error) {
      console.error('Error saving pain score:', error);
      showSnackbar('Failed to save pain assessment', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordVitals = () => {
    setOpenDialog('vitals');
  };

  const handleSaveVitals = async () => {
    const requiredFields = ['bloodPressureSystolic', 'bloodPressureDiastolic', 'heartRate'];
    const missing = requiredFields.filter(field => !vitalSigns[field as keyof typeof vitalSigns]);
    
    if (missing.length > 0) {
      showSnackbar('Please fill in all required vital signs', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare vital signs data
      const vitalsData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        vitalSigns: vitalSigns,
        recordedBy: user?.name || 'Unknown',
        recoveryTime: recoveryTime
      };

      // Use service to create vital signs
      await surgicalPhasesService.createVitalSigns(vitalsData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, vitalSigns: 'completed' }));
      setOpenDialog(null);
      showSnackbar('Vital signs recorded successfully');
    } catch (error) {
      console.error('Error saving vital signs:', error);
      showSnackbar('Failed to save vital signs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFluidBalance = () => {
    setOpenDialog('fluids');
  };

  const handleSaveFluidBalance = async () => {
    if (!fluidIntake && !fluidOutput) {
      showSnackbar('Please enter fluid intake or output data', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare fluid balance data
      const fluidData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        fluidIntake: parseFloat(fluidIntake) || 0,
        fluidOutput: parseFloat(fluidOutput) || 0,
        netBalance: (parseFloat(fluidIntake) || 0) - (parseFloat(fluidOutput) || 0),
        recordedBy: user?.name || 'Unknown'
      };

      // Use service to create fluid balance
      await surgicalPhasesService.createFluidBalance(fluidData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, fluidBalance: 'completed' }));
      setOpenDialog(null);
      setFluidIntake('');
      setFluidOutput('');
      showSnackbar('Fluid balance updated successfully');
    } catch (error) {
      console.error('Error saving fluid balance:', error);
      showSnackbar('Failed to save fluid balance', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteAldreteScore = () => {
    setOpenDialog('aldrete');
  };

  const handleSaveAldreteScore = async () => {
    const totalScore = Object.values(aldreteComponents).reduce((sum, score) => sum + score, 0);
    setAldreteScore(totalScore);

    try {
      setLoading(true);

      // Prepare Aldrete score data
      const aldreteData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        aldreteComponents: aldreteComponents,
        totalScore: totalScore,
        maxScore: 10,
        dischargeReady: totalScore >= 9,
        assessedBy: user?.name || 'Unknown',
        notes: dischargeNotes
      };

      // Use service to create Aldrete score
      await surgicalPhasesService.createAldreteScore(aldreteData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, dischargeReadiness: 'completed' }));
      setOpenDialog(null);
      setDischargeNotes('');
      showSnackbar(`Aldrete score ${totalScore}/10 completed - ${totalScore >= 9 ? 'Ready for discharge' : 'Continue monitoring'}`);
    } catch (error) {
      console.error('Error saving Aldrete score:', error);
      showSnackbar('Failed to save Aldrete score', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearForDischarge = async () => {
    const incomplete = Object.entries(checklistStatus).filter(([_, status]) => status !== 'completed');
    if (incomplete.length > 0) {
      showSnackbar('Please complete all checklist items before discharge', 'error');
      return;
    }

    if (aldreteScore < 9) {
      showSnackbar('Aldrete score must be ≥9 for discharge clearance', 'error');
      return;
    }

    try {
      setLoading(true);

      // Update surgical case status
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'pacu', {
          status: 'completed',
          dischargeTime: createTimestamp(),
          completedBy: user?.uid,
          aldreteScore: aldreteScore
        });

        await surgicalCaseService.updateCaseStatus(caseId, 'cleared_for_discharge', 'postOp');
      }

      // Prepare discharge clearance data
      const clearanceData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        aldreteScore: aldreteScore,
        totalRecoveryTime: recoveryTime,
        checklistItems: checklistStatus
      };

      // Use service to create discharge clearance
      await surgicalPhasesService.createDischargeClearance(clearanceData);

      showSnackbar('Patient cleared for discharge from PACU');
    } catch (error) {
      console.error('Error clearing for discharge:', error);
      showSnackbar('Failed to clear for discharge', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferToUnit = async () => {
    try {
      setLoading(true);

      // Prepare transfer request data
      const transferData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        transferType: 'unit_admission',
        aldreteScore: aldreteScore,
        recoveryTime: recoveryTime,
        reason: 'Requires continued monitoring and care'
      };

      // Use service to create transfer request
      await surgicalPhasesService.createTransferRequest(transferData);

      showSnackbar('Transfer to unit requested - Awaiting bed assignment');
    } catch (error) {
      console.error('Error requesting transfer:', error);
      showSnackbar('Failed to request transfer', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReport = async () => {
    try {
      setLoading(true);

      // Prepare print request data
      const printData = {
        documentType: 'pacu_recovery_report',
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        reportData: {
          painScore: painScore,
          aldreteScore: aldreteScore,
          vitalSigns: vitalSigns,
          recoveryTime: recoveryTime,
          checklistStatus: checklistStatus
        }
      };

      // Use service to create print request
      await surgicalPhasesService.createPrintRequest(printData);

      showSnackbar('PACU recovery report sent to printer');
    } catch (error) {
      console.error('Error printing report:', error);
      showSnackbar('Failed to send report to printer', 'error');
    } finally {
      setLoading(false);
    }
  };

  const statusIcon = (status: string) => {
    if (status === 'completed') {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32, mr: 2 }} />;
    }
    return <CancelIcon sx={{ color: 'error.main', fontSize: 32, mr: 2 }} />;
  };

  const checklist = [
    {
      key: 'postOpAssessment',
      title: 'Post-Op Assessment',
      status: checklistStatus.postOpAssessment,
      description: 'Complete initial recovery assessment',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleCompleteAssessment}
          disabled={loading}
        >
          Complete Assessment
        </Button>
      )
    },
    {
      key: 'painManagement',
      title: 'Pain Management',
      status: checklistStatus.painManagement,
      description: 'Document pain score and interventions',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleDocumentPainScore}
          disabled={loading}
        >
          Document Pain Score
        </Button>
      )
    },
    {
      key: 'vitalSigns',
      title: 'Vital Signs Monitoring',
      status: checklistStatus.vitalSigns,
      description: 'Record current vital signs',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleRecordVitals}
          disabled={loading}
        >
          Record Vitals
        </Button>
      )
    },
    {
      key: 'fluidBalance',
      title: 'Fluid Balance',
      status: checklistStatus.fluidBalance,
      description: 'Update intake and output records',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleUpdateFluidBalance}
          disabled={loading}
        >
          Update I/O
        </Button>
      )
    },
    {
      key: 'dischargeReadiness',
      title: 'Discharge Readiness Assessment',
      status: checklistStatus.dischargeReadiness,
      description: 'Complete Aldrete scoring system',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleCompleteAldreteScore}
          disabled={loading}
        >
          Complete Aldrete Score
        </Button>
      )
    }
  ];

  return (
    <Box sx={{ p: 0 }}>
      {/* Status Bar */}
      <Card sx={{ mb: 3, borderRadius: 2, background: '#f0f8ff' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              PACU Status:
            </Typography>
            <Chip
              label={surgicalProcedure.status === 'completed' ? 'Recovery Complete' : 'In Recovery'}
              color={surgicalProcedure.status === 'completed' ? 'success' : 'info'}
              size="small"
              sx={{ ml: 1, fontWeight: 600, fontSize: 14 }}
              icon={<LocalHospitalIcon sx={{ fontSize: 18 }} />}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Recovery Time: {recoveryTime} mins
          </Typography>
        </CardContent>
      </Card>

      {/* Recovery Metrics */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Recovery Metrics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Pain Score</Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: painScore > 5 ? '#d32f2f' : painScore > 0 ? '#ff9800' : '#1976d2' 
                }}>
                  {painScore || '--'}/10
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Aldrete Score</Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: aldreteScore >= 9 ? '#4caf50' : aldreteScore >= 7 ? '#ff9800' : '#d32f2f' 
                }}>
                  {aldreteScore || '--'}/10
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Blood Pressure</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {vitalSigns.bloodPressureSystolic && vitalSigns.bloodPressureDiastolic 
                    ? `${vitalSigns.bloodPressureSystolic}/${vitalSigns.bloodPressureDiastolic} mmHg` 
                    : '-- / -- mmHg'
                  }
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Heart Rate</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {vitalSigns.heartRate ? `${vitalSigns.heartRate} bpm` : '-- bpm'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* PACU Checklist */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        PACU Checklist
      </Typography>
      <Stack spacing={2}>
        {checklist.map(item => (
          <Card key={item.key} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {statusIcon(item.status)}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.status === 'completed' ? 'Completed' : item.description}
                  </Typography>
                </Box>
              </Box>
              <Box>
                {item.action}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Sticky Footer Actions */}
      <Box sx={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        width: '100%',
        background: '#f0f8ff',
        borderTop: '1px solid #e0e0e0',
        mt: 4,
        py: 2,
        zIndex: 10
      }}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Button 
            variant="contained" 
            color="success" 
            sx={{ minWidth: 180 }}
            onClick={handleClearForDischarge}
            disabled={loading}
          >
            Clear for Discharge
          </Button>
          <Button 
            variant="contained" 
            color="warning" 
            sx={{ minWidth: 180 }}
            onClick={handleTransferToUnit}
            disabled={loading}
            startIcon={<TransferWithinAStationIcon />}
          >
            Transfer to Unit
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ minWidth: 140 }}
            onClick={handlePrintReport}
            disabled={loading}
            startIcon={<PrintIcon />}
          >
            Print Report
          </Button>
        </Stack>
      </Box>

      {/* Assessment Dialog */}
      <Dialog open={openDialog === 'assessment'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Complete Post-Operative Assessment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Assessment Notes"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={assessmentNotes}
            onChange={(e) => setAssessmentNotes(e.target.value)}
            placeholder="Document patient's neurological status, respiratory effort, circulation, pain level, nausea/vomiting, surgical site..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveAssessment} variant="contained" disabled={loading}>
            Save Assessment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pain Score Dialog */}
      <Dialog open={openDialog === 'pain'} onClose={() => setOpenDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Document Pain Score</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography gutterBottom>Pain Score (0-10 scale)</Typography>
            <Slider
              value={painScore}
              onChange={(_, newValue) => setPainScore(newValue as number)}
              min={0}
              max={10}
              marks
              step={1}
              valueLabelDisplay="on"
              sx={{ mt: 2 }}
            />
          </Box>
          <TextField
            label="Pain Management Notes"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={painNotes}
            onChange={(e) => setPainNotes(e.target.value)}
            placeholder="Document pain location, character, interventions provided, patient response..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSavePainScore} variant="contained" disabled={loading}>
            Save Pain Score
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vital Signs Dialog */}
      <Dialog open={openDialog === 'vitals'} onClose={() => setOpenDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Vital Signs</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Systolic BP"
                  type="number"
                  value={vitalSigns.bloodPressureSystolic}
                  onChange={(e) => setVitalSigns(prev => ({ ...prev, bloodPressureSystolic: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Diastolic BP"
                  type="number"
                  value={vitalSigns.bloodPressureDiastolic}
                  onChange={(e) => setVitalSigns(prev => ({ ...prev, bloodPressureDiastolic: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Heart Rate"
                  type="number"
                  value={vitalSigns.heartRate}
                  onChange={(e) => setVitalSigns(prev => ({ ...prev, heartRate: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Respiratory Rate"
                  type="number"
                  value={vitalSigns.respiratoryRate}
                  onChange={(e) => setVitalSigns(prev => ({ ...prev, respiratoryRate: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="O2 Saturation (%)"
                  type="number"
                  value={vitalSigns.oxygenSaturation}
                  onChange={(e) => setVitalSigns(prev => ({ ...prev, oxygenSaturation: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Temperature (°F)"
                  type="number"
                  value={vitalSigns.temperature}
                  onChange={(e) => setVitalSigns(prev => ({ ...prev, temperature: e.target.value }))}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveVitals} variant="contained" disabled={loading}>
            Save Vital Signs
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fluid Balance Dialog */}
      <Dialog open={openDialog === 'fluids'} onClose={() => setOpenDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Fluid Balance</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Fluid Intake (mL)"
              type="number"
              value={fluidIntake}
              onChange={(e) => setFluidIntake(e.target.value)}
              fullWidth
              placeholder="Total IV fluids, oral intake, etc."
            />
            <TextField
              label="Fluid Output (mL)"
              type="number"
              value={fluidOutput}
              onChange={(e) => setFluidOutput(e.target.value)}
              fullWidth
              placeholder="Urine output, drains, etc."
            />
            {fluidIntake && fluidOutput && (
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2">Net Balance:</Typography>
                <Typography variant="h6" sx={{ 
                  color: (parseFloat(fluidIntake) - parseFloat(fluidOutput)) >= 0 ? '#4caf50' : '#f44336' 
                }}>
                  {(parseFloat(fluidIntake) - parseFloat(fluidOutput)).toFixed(0)} mL
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveFluidBalance} variant="contained" disabled={loading}>
            Save Fluid Balance
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aldrete Score Dialog */}
      <Dialog open={openDialog === 'aldrete'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Complete Aldrete Score Assessment</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Assessment</strong></TableCell>
                  <TableCell><strong>Criteria</strong></TableCell>
                  <TableCell><strong>Score</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Activity</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      2 = Moves 4 extremities voluntarily<br/>
                      1 = Moves 2 extremities voluntarily<br/>
                      0 = Unable to move extremities
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={aldreteComponents.activity}
                      onChange={(e) => setAldreteComponents(prev => ({ ...prev, activity: e.target.value as number }))}
                      size="small"
                    >
                      <MenuItem value={0}>0</MenuItem>
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Respiration</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      2 = Breathes deeply and coughs freely<br/>
                      1 = Dyspnea, shallow breathing<br/>
                      0 = Apneic
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={aldreteComponents.respiration}
                      onChange={(e) => setAldreteComponents(prev => ({ ...prev, respiration: e.target.value as number }))}
                      size="small"
                    >
                      <MenuItem value={0}>0</MenuItem>
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Circulation</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      2 = BP ± 20 mmHg of pre-anesthetic level<br/>
                      1 = BP ± 20-50 mmHg of pre-anesthetic level<br/>
                      0 = BP ± 50 mmHg of pre-anesthetic level
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={aldreteComponents.circulation}
                      onChange={(e) => setAldreteComponents(prev => ({ ...prev, circulation: e.target.value as number }))}
                      size="small"
                    >
                      <MenuItem value={0}>0</MenuItem>
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Consciousness</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      2 = Fully awake<br/>
                      1 = Arousable on calling<br/>
                      0 = Not responding
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={aldreteComponents.consciousness}
                      onChange={(e) => setAldreteComponents(prev => ({ ...prev, consciousness: e.target.value as number }))}
                      size="small"
                    >
                      <MenuItem value={0}>0</MenuItem>
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>O2 Saturation</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      2 = Maintains SpO2 &gt;92% on room air<br/>
                      1 = Needs O2 inhalation to maintain SpO2 &gt;90%<br/>
                      0 = SpO2 &lt;90% even with O2 supplement
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={aldreteComponents.oxygenSaturation}
                      onChange={(e) => setAldreteComponents(prev => ({ ...prev, oxygenSaturation: e.target.value as number }))}
                      size="small"
                    >
                      <MenuItem value={0}>0</MenuItem>
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="h6">
              Total Score: {Object.values(aldreteComponents).reduce((sum, score) => sum + score, 0)}/10
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Score ≥9 required for discharge
            </Typography>
          </Box>
          <TextField
            label="Additional Notes"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={dischargeNotes}
            onChange={(e) => setDischargeNotes(e.target.value)}
            placeholder="Any additional observations or concerns..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveAldreteScore} variant="contained" disabled={loading}>
            Save Aldrete Score
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurgicalPACUPhase; 